import { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX, Loader2, Pause, Play, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = window.location.origin;

// Available voices with friendly names
const VOICES = {
  nova: { name: 'Nova', description: 'Energetic, upbeat' },
  alloy: { name: 'Alloy', description: 'Neutral, balanced' },
  coral: { name: 'Coral', description: 'Warm, friendly' },
  sage: { name: 'Sage', description: 'Wise, measured' },
  shimmer: { name: 'Shimmer', description: 'Bright, cheerful' },
  echo: { name: 'Echo', description: 'Smooth, calm' },
  fable: { name: 'Fable', description: 'Expressive, storytelling' },
  onyx: { name: 'Onyx', description: 'Deep, authoritative' },
  ash: { name: 'Ash', description: 'Clear, articulate' }
};

/**
 * Text to Speech Component - Reads text aloud
 * @param {string} text - Text to convert to speech
 * @param {string} entryId - Optional journal entry ID for direct API call
 * @param {string} label - Button label
 * @param {string} variant - Button variant
 * @param {string} size - Button size
 */
export const TextToSpeech = ({ 
  text, 
  entryId,
  label = 'Listen',
  variant = 'ghost',
  size = 'sm'
}) => {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [voice, setVoice] = useState('nova');
  const [speed, setSpeed] = useState(1.0);
  const audioRef = useRef(null);

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleSpeak = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }

    if (!text && !entryId) {
      toast.error('No text to read');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      let response;

      if (entryId) {
        // Use the journal entry endpoint
        response = await axios.post(
          `${API_URL}/api/voice/journal-entry/${entryId}/speak`,
          null,
          {
            params: { voice, speed },
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
      } else {
        // Use the general TTS endpoint
        response = await axios.post(
          `${API_URL}/api/voice/speak`,
          { text, voice, speed },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      if (response.data.success && response.data.audio) {
        // Create audio from base64
        const audioSrc = `data:audio/mp3;base64,${response.data.audio}`;
        
        // Stop any existing audio
        if (audioRef.current) {
          audioRef.current.pause();
        }
        
        const audio = new Audio(audioSrc);
        audioRef.current = audio;
        
        audio.onended = () => {
          setPlaying(false);
        };
        
        audio.onerror = () => {
          toast.error('Error playing audio');
          setPlaying(false);
        };

        await audio.play();
        setPlaying(true);
      }
    } catch (err) {
      console.error('TTS error:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate speech');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        variant={variant}
        size={size}
        onClick={playing ? handleStop : handleSpeak}
        disabled={loading || (!text && !entryId)}
        className={playing ? 'text-terracotta' : 'text-forest hover:text-terracotta'}
        data-testid="tts-btn"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : playing ? (
          <>
            <Pause className="h-4 w-4 mr-1" />
            Stop
          </>
        ) : (
          <>
            <Volume2 className="h-4 w-4 mr-1" />
            {label}
          </>
        )}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="px-2" data-testid="tts-settings-btn">
            <Settings className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Voice Settings</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuLabel className="text-xs text-gray-500 font-normal">Voice</DropdownMenuLabel>
          {Object.entries(VOICES).map(([key, { name, description }]) => (
            <DropdownMenuItem
              key={key}
              onClick={() => setVoice(key)}
              className={voice === key ? 'bg-forest/10' : ''}
            >
              <div className="flex flex-col">
                <span className={voice === key ? 'text-forest font-medium' : ''}>
                  {name}
                </span>
                <span className="text-xs text-gray-500">{description}</span>
              </div>
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-gray-500 font-normal">Speed</DropdownMenuLabel>
          {[0.75, 1.0, 1.25, 1.5].map((s) => (
            <DropdownMenuItem
              key={s}
              onClick={() => setSpeed(s)}
              className={speed === s ? 'bg-forest/10 text-forest font-medium' : ''}
            >
              {s}x {s === 1.0 ? '(Normal)' : s < 1 ? '(Slower)' : '(Faster)'}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default TextToSpeech;
